import React, { useEffect, useState } from "react";
import { useGetList } from "react-admin";
import GoogleMapsDialog from "./integrations/GoogleMapsDialog";
import SendGridDialog from "./integrations/SendGridDialog";
import VoipDialog from "./integrations/VoipDialog";
import { useUserContext } from "@/context/UserContext";
import axiosInstance from "@/utils/axiosInstance";
import { Integration } from "@/interface/entityInterface";
import DeleteVoip from "./integrations/VoipDelete";
import ResendDialog from "./integrations/ResendDialog";

const integrations = [
  {
    key: "googlemaps",
    name: "Google Maps",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/bd/Google_Maps_Logo_2020.svg/2275px-Google_Maps_Logo_2020.svg.png",
    description: "Integrate location services via Google Maps.",
  },
  {
    key: "mapbox",
    name: "Map Box",
    logo: "https://wpmaps.com/wp-content/themes/wpm/images/bk/mapbox.png",
    description: "Integrate location services via Map Box.",
  },
  {
    key: "sendgrid",
    name: "SendGrid",
    logo: "https://dkgakr8jhpwe5.cloudfront.net/static/SG_Twilio_Lockup_Social-56f3cfd2f6b0c62422980170d57fac64.png",
    description: "Enable email sending with SendGrid.",
  },
  {
    key: "resend",
    name: "Resend",
    logo: "https://cdn.resend.com/brand/resend-icon-black.png",
    description: "Enable email sending with Resend.",
  },
  {
    key: "voip",
    name: "Voip",
    logo: "https://i.pinimg.com/736x/2e/38/55/2e385501fd9142bacf1f2d188452d82c.jpg",
    description: "Enable call sessions",
  },
];

const Integrations: React.FC = () => {
  const { decodedToken } = useUserContext();
  const [status, setStatus] = useState<Record<string, any>>();
  const [freepbxId, setId] = useState(0);

  const { data } = useGetList("integrations", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "published_at", order: "DESC" },
  });

  useEffect(() => {
    if (data) {
      const integrationsMap: Record<string, any[]> = {};

      data.forEach((item: any) => {
        const key = item.key;

        if (!integrationsMap[key]) {
          integrationsMap[key] = [];
        }

        integrationsMap[key].push(item);
      });

      setStatus(integrationsMap);
    }
  }, [data]);

  useEffect(() => {
    if (!decodedToken) return;
    const getIntegrations = async () => {
      const { data } = await axiosInstance.get<Integration[]>(`/integrations`);
      const freepbxIntegration = data.find((item) => item.key === "freepbx");
      if (freepbxIntegration) setId(freepbxIntegration.id);
    };

    getIntegrations();
  }, [decodedToken]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
      {integrations.map((integration) => {
        const isEnabled = status?.[integration.key];
        return (
          <div
            key={integration.name}
            className="border rounded-xl p-6 shadow-sm flex flex-col items-center text-center dark:bg-[#222]"
          >
            <img
              src={integration.logo}
              alt={integration.name}
              className="h-16 w-full object-scale-down mb-4"
            />
            <h3 className="text-lg font-semibold">{integration.name}</h3>
            <p className="text-sm text-gray-500 mb-4">
              {integration.description}
            </p>

            {integration.key === "googlemaps" && (
              <GoogleMapsDialog isEnabled={!!isEnabled} config={isEnabled} />
            )}
            {integration.key === "sendgrid" && (
              <SendGridDialog isEnabled={!!isEnabled} config={isEnabled} />
            )}
            {integration.key === "resend" && (
              <ResendDialog isEnabled={!!isEnabled} config={isEnabled} />
            )}
            {integration.key === "voip" && (
              <>
                {freepbxId ? (
                  <div className="flex justify-center itemsp-center gap-x-4">
                    <VoipDialog id={freepbxId} title="Edit" />
                    <DeleteVoip prop={freepbxId} />
                  </div>
                ) : (
                  <VoipDialog title="Integrate" />
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Integrations;
